import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";

function fmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffDays = Math.floor((Date.now() - d.getTime()) / 86_400_000);
    if (diffDays < 1) return "today";
    if (diffDays === 1) return "yesterday";
    if (diffDays < 30) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function WhoPinnedThis({ conceptId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!conceptId) return;
    api.get(`/pins/public/${conceptId}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ count: 0, items: [] }));
  }, [conceptId]);

  if (!data || data.count === 0) return null;

  return (
    <section data-testid="who-pinned" className="mt-16">
      <button
        data-testid="who-pinned-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full hairline-t pt-6 flex items-center justify-between text-left group"
      >
        <div className="flex items-center gap-3">
          <Bookmark size={14} className="text-[hsl(var(--primary))]" fill="currentColor" />
          <span className="mono text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
            {data.count} reader{data.count === 1 ? "" : "s"} pinned this publicly
          </span>
        </div>
        {open ? (
          <ChevronUp size={14} className="text-muted-foreground group-hover:text-foreground transition-colors" />
        ) : (
          <ChevronDown size={14} className="text-muted-foreground group-hover:text-foreground transition-colors" />
        )}
      </button>

      {open && (
        <ul className="mt-6 space-y-5" data-testid="who-pinned-list">
          {data.items.map((p, i) => {
            const slug = p.user_handle || p.user_id;
            const NameWrap = slug ? Link : "span";
            const wrapProps = slug ? { to: `/u/${slug}`, className: "link-underline" } : {};
            // Stable key: user_id + created_at guarantees no collision even
            // if the same user somehow appears twice. Fall back to index only
            // when both fields are absent (shouldn't happen in prod data).
            const stableKey = (p.user_id && p.created_at) ? `${p.user_id}-${p.created_at}` : `pin-${i}`;
            return (
              <li key={stableKey} className="flex items-start gap-3 hairline-b pb-5 last:border-b-0">
                {p.user_picture ? (
                  <img src={p.user_picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-sm">
                    {(p.user_name || "?")[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <NameWrap {...wrapProps}>
                      <span className="serif text-foreground">
                        {p.user_name || "A reader"}
                      </span>
                    </NameWrap>
                    {p.user_handle && (
                      <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
                        @{p.user_handle}
                      </span>
                    )}
                    <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
                      {fmt(p.created_at)}
                    </span>
                  </div>
                  {p.note && (
                    <p className="mt-1 text-sm text-muted-foreground italic">"{p.note}"</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
